import type { LadderTemplate, LadderTemplateMatch } from '../types';

export const POOL_PLAYOFFS_TEMPLATE_ID = 'pool-playoffs';

/** Human label for the match that decides positions `hi` (winner) and `lo` (loser). */
export function placementLabel(hi: number, lo: number): string {
  if (hi === 1) return 'Final';
  if (hi === 3) return '3rd/4th Playoff';
  return `${hi}/${lo} Playoff`;
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

/**
 * Build the placement ladder for two pools.
 * - Pairs A_r vs B_r for r in 1..min(sizeA,sizeB); each decides positions (2r-1, 2r).
 * - Snake-draft guarantees |sizeA - sizeB| <= 1, so at most one orphan exists.
 *   When sizes differ, the larger pool's lowest finisher byes to the last position.
 * - All matches are round 1 and independent (no match->match progression).
 */
export function buildPlacementLadder(sizeA: number, sizeB: number): LadderTemplate {
  // Snake-draft into 2 pools guarantees |sizeA - sizeB| <= 1 (at most one orphan).
  // Assert the contract so a misuse can't silently emit a bracket that drops players.
  if (Math.abs(sizeA - sizeB) > 1) {
    throw new Error(
      `buildPlacementLadder: pool sizes must differ by at most 1, got ${sizeA} and ${sizeB}`,
    );
  }
  const paired = Math.min(sizeA, sizeB);
  const matches: LadderTemplateMatch[] = [];

  for (let r = 1; r <= paired; r++) {
    const hi = 2 * r - 1;
    const lo = 2 * r;
    matches.push({
      id: `place-${hi}-${lo}`,
      label: placementLabel(hi, lo),
      round: 1,
      player1: { from: 'standings', pool: 'A', position: r },
      player2: { from: 'standings', pool: 'B', position: r },
    });
  }

  if (sizeA !== sizeB) {
    const orphanPool: 'A' | 'B' = sizeA > sizeB ? 'A' : 'B';
    const orphanRank = Math.max(sizeA, sizeB); // = paired + 1
    const lastPos = sizeA + sizeB;
    matches.push({
      id: `place-${lastPos}`,
      label: `${ordinal(lastPos)} Place`,
      round: 1,
      player1: { from: 'standings', pool: orphanPool, position: orphanRank },
      player2: { from: 'bye' },
    });
  }

  return {
    id: POOL_PLAYOFFS_TEMPLATE_ID,
    name: 'Pool Play-offs',
    requiredStandings: paired, // per-pool minimum
    matches,
  };
}
